import { useEffect } from 'react';
import {
  clampScale,
  customBackgroundId,
  DEFAULT_BODY_FONT,
  DEFAULT_HEADING_FONT,
  fontById,
  googleFontsUrl,
} from '@td/shared';
import { useAuth } from '../auth/AuthContext';
import { backgroundColor, backgroundImage, textureImage } from './backgrounds';
import { setDisplayTimeZone } from '../lib/dates';
import { channels, headerShades, setPalette, themeById } from './palette';

/**
 * Puts the teacher's saved appearance — fonts, background, time zone — onto
 * the page. Renders nothing; it only sets CSS variables and module state, so
 * every component picks the change up without being told about it.
 */
export function AppearanceProvider() {
  const { user } = useAuth();

  const body = user?.settings.bodyFont ?? DEFAULT_BODY_FONT;
  const heading = user?.settings.headingFont ?? DEFAULT_HEADING_FONT;
  const background = user?.settings.background ?? null;
  const texture = user?.settings.backgroundTexture ?? null;
  const textureStrength = user?.settings.textureStrength ?? undefined;
  const timeZone = user?.settings.timeZone ?? null;
  const themeId = user?.settings.theme ?? 'boho';
  const textScale = clampScale(user?.settings.textScale);
  const headingScale = clampScale(user?.settings.headingScale);
  const headerStyle = user?.settings.headerStyle ?? 'plain';
  const headerColor = user?.settings.headerColor ?? null;

  /* ------------------------------------------------------------- theme */
  useEffect(() => {
    const theme = themeById(themeId);
    const root = document.documentElement;

    // Fourteen variables, and every Tailwind colour class in the app follows.
    for (const [name, hex] of Object.entries(theme.palette)) {
      root.style.setProperty(`--c-${name}`, channels(hex));
    }

    // Also kept in module state, for the parts that need a real hex rather
    // than a class: card accents, the sidebar's per-section colours.
    setPalette(theme.palette);
  }, [themeId]);

  /* ------------------------------------------------------------- fonts */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--font-body', fontById(body)?.stack ?? "'Nunito Sans'");
    root.style.setProperty('--font-heading', fontById(heading)?.stack ?? "'Fraunces'");

    const href = googleFontsUrl([body, heading]);
    if (!href) return;

    // One link element, swapped as the choice changes, rather than a growing
    // pile of stylesheets.
    let link = document.getElementById('td-fonts') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = 'td-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  }, [body, heading]);

  /* -------------------------------------------------------- background */
  useEffect(() => {
    const root = document.documentElement;
    const custom = customBackgroundId(background);

    if (custom) {
      // A photograph is covered and centred once; a drawn tile repeats. The
      // image route is authenticated, and a same-origin background-image
      // request carries the session cookie like any other subresource.
      root.style.setProperty('--app-pattern', `url("/api/backgrounds/${custom}/image")`);
      root.style.setProperty('--app-pattern-size', 'cover');
      root.style.setProperty('--app-pattern-repeat', 'no-repeat');
    } else {
      root.style.setProperty('--app-pattern', backgroundImage(background));
      root.style.setProperty('--app-pattern-size', 'auto');
      root.style.setProperty('--app-pattern-repeat', 'repeat');
    }

    // Plain and woven backgrounds change the paper itself, not just what is
    // drawn on it. A photograph covers the page anyway, so it keeps cream
    // underneath for the moment before the image loads.
    /*
     * The theme's palette, passed rather than read: this effect and the one
     * that publishes the palette both fire on a theme change, and their order
     * should not decide what colour the page is.
     */
    const p = themeById(themeId).palette;
    root.style.setProperty('--app-bg-color', custom ? p.cream : backgroundColor(background, p));

    // A separate layer on top of whatever the background is, so the two
    // choices compose instead of multiplying into one enormous list.
    root.style.setProperty('--app-texture', textureImage(texture, textureStrength));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background, texture, textureStrength, themeId]);

  /* ------------------------------------------------------------- sizing */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--text-scale', String(textScale));
    root.style.setProperty('--heading-scale', String(headingScale));
  }, [textScale, headingScale]);

  /* ------------------------------------------------------------ headers */

  /*
   * An attribute on the root rather than a class on every header.
   *
   * There are fourteen page headers and they are written independently; a
   * prop threaded through all of them is fourteen chances to forget one. The
   * whole switch is one CSS rule that reads this.
   */
  useEffect(() => {
    document.documentElement.dataset.header = headerStyle;
  }, [headerStyle]);

  /*
   * And which colour it wears.
   *
   * The header always paints with `--header-*`, and those default to the
   * section's `--accent-*` in the stylesheet. Setting a colour here writes
   * concrete values over that; clearing it removes them, and the header goes
   * back to following the page it is on. No page needs to know either way.
   *
   * The shades are derived from the theme's palette rather than stored, so
   * `mauve` is this theme's mauve — and a header set once stays sensible
   * through every theme change afterwards.
   */
  useEffect(() => {
    const root = document.documentElement;
    const names = ['wash', 'tint', 'base', 'label', 'deep', 'press', 'fill'] as const;

    if (!headerColor) {
      for (const name of names) root.style.removeProperty(`--header-${name}`);
      return;
    }

    const shades = headerShades(headerColor);
    for (const name of names) root.style.setProperty(`--header-${name}`, shades[name]);
  }, [headerColor, themeId]);

  /* --------------------------------------------------------- time zone */
  useEffect(() => {
    setDisplayTimeZone(timeZone);
    // Dates are read during render, so a changed zone needs a repaint of
    // anything already on screen. A single storage-style event is cheaper
    // than threading the zone through every component.
    window.dispatchEvent(new Event('td:timezone'));
  }, [timeZone]);

  return null;
}

/**
 * Loads every font in the picker so each sample is shown in the font it names.
 * Mounted only by Settings, and only while that page is open.
 */
export function useFontSamples(ids: string[]) {
  const key = ids.join(',');

  useEffect(() => {
    const href = googleFontsUrl(key.split(','));
    if (!href) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);

    return () => link.remove();
  }, [key]);
}
